import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/admin/AdminLayout'
import { useAuth } from '../../context/AuthContext'
import api from '../../utils/api'
import { Modal, Button, Form } from 'react-bootstrap'
import { toast } from 'react-toastify'
import ConfirmModal from '../../components/modals/ConfirmModal'
import login from './login'

interface UserRecord {
    _id: string; name: string; email: string; role: string;
    phone?: string; city?: string; createdAt?: string;
    username?: string; title?: string; about?: string;
    assignedPackage?: {
        _id: string;
        name: string;
        type: string;
    };
    packageStartDate?: string;
    packageExpiry?: string;
    maxDevices?: number;
}

export default function AdminUsers() {
    const { token, loginWithToken } = useAuth();
    
    const [users, setUsers] = useState<UserRecord[]>([])
    const [packages, setPackages] = useState<any[]>([])
    const [loading, setLoading] = useState(true)
    const [deleting, setDeleting] = useState<string | null>(null)
    const [changingRole, setChangingRole] = useState<string | null>(null)
    const [search, setSearch] = useState('')
    const [roleFilter, setRoleFilter] = useState('all')
    const [currentPage, setCurrentPage] = useState(1)
    const [itemsPerPage, setItemsPerPage] = useState(10)
    const [confirmModal, setConfirmModal] = useState<{ 
        isOpen: boolean; 
        id: string; 
        name: string; 
        type: 'user' | 'package' | 'bulk_user'
    }>({ isOpen: false, id: '', name: '', type: 'user' });

    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [isDeletingMultiple, setIsDeletingMultiple] = useState(false);

const handleLoginAsUser = async (userId: string) => {
    try {
        const res = await api.adminLoginAsUser(token!, userId);

        console.log("API RESPONSE:", res);

        if (res?.token) {
            await loginWithToken(res.token);   // ✅ correct
            window.location.href = "/";        // ✅ redirect
        } else {
            alert(res.message || "Login failed");
        }

    } catch (err) {
        console.error(err);
        alert("Server error");
    }
};
    // Package Assignment Modal
    const [showPkgModal, setShowPkgModal] = useState(false)
    const [selectedUser, setSelectedUser] = useState<UserRecord | null>(null)
    const [selectedPkgId, setSelectedPkgId] = useState('')
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [assigning, setAssigning] = useState(false)

    // User Edit Modal
    const [showEditModal, setShowEditModal] = useState(false)
    const [editingUser, setEditingUser] = useState<Partial<UserRecord>>({})
    const [updating, setUpdating] = useState(false)

    useEffect(() => {
        if (!token) { setLoading(false); return }
        loadData()
    }, [token])

    const loadData = async () => {
        setLoading(true)
        try {
            const [userData, pkgData] = await Promise.all([
                api.adminGetUsers(token!),
                api.getPackages()
            ])
            if (Array.isArray(userData)) setUsers(userData)
            if (Array.isArray(pkgData)) setPackages(pkgData)
        } catch (error) {
            console.error(error)
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = async () => {
        const { id, name } = confirmModal;
        if (!token || !id) return
        setDeleting(id)
        setConfirmModal({ ...confirmModal, isOpen: false });
        const res = await api.adminDeleteUser(token, id)
        if (res.message === 'User deleted successfully') {
            setUsers(prev => prev.filter(u => u._id !== id))
            toast.success(`Identity "${name}" has been purged from records.`)
        } else {
            toast.error(res.message || 'Purge failed.')
        }
        setDeleting(null)
    }

    const handleRoleChange = async (id: string, newRole: string) => {
        if (!token) return
        setChangingRole(id)
        const res = await api.adminChangeRole(token, id, newRole)
        if (res._id) {
            setUsers(prev => prev.map(u => u._id === id ? { ...u, role: newRole } : u))
            toast.success(`Access level elevated to "${newRole}".`)
        } else {
            toast.error(res.message || 'Privilege update failed.')
        }
        setChangingRole(null)
    }

    const handleOpenPkgModal = (user: UserRecord) => {
        setSelectedUser(user)
        setSelectedPkgId(user.assignedPackage?._id || '')
        setStartDate('')
        setEndDate('')
        setShowPkgModal(true)
    }

    const handleAssignPkg = async () => {
        if (!selectedUser || !selectedPkgId || !token) return
        setAssigning(true)
        try {
            const res = await api.adminAssignPackage(token, selectedUser._id, selectedPkgId, startDate, endDate)
            if (res.user) {
                setUsers(prev => prev.map(u => u._id === selectedUser._id ? { ...u, assignedPackage: res.user.assignedPackage } : u))
                toast.success(`Package "${res.user.assignedPackage.name}" assigned to ${selectedUser.name}`)
                setShowPkgModal(false)
                setStartDate('')
                setEndDate('')
            } else {
                toast.error(res.message || 'Assignment failed')
            }
        } catch (error) {
            toast.error('Server error')
        } finally {
            setAssigning(false)
        }
    }

    const handleUnassignPkg = async () => {
        if (!selectedUser || !token) return
        setAssigning(true)
        setConfirmModal({ ...confirmModal, isOpen: false });
        try {
            const res = await api.adminUnassignPackage(token, selectedUser._id)
            if (res.user) {
                setUsers(prev => prev.map(u => u._id === selectedUser._id ? { ...u, assignedPackage: undefined } : u))
                toast.success(`Package removed from ${selectedUser.name}`)
                setShowPkgModal(false)
            } else {
                toast.error(res.message || 'Unassignment failed')
            }
        } catch (error) {
            toast.error('Server error')
        } finally {
            setAssigning(false)
        }
    }

    const handleOpenEditModal = (user: UserRecord) => {
        setEditingUser({ ...user })
        setShowEditModal(true)
    }

    const handleEditSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!editingUser._id || !token) return
        setUpdating(true)
        try {
            const res = await api.adminUpdateUser(token, editingUser._id, editingUser)
            if (res._id) {
                setUsers(prev => prev.map(u => u._id === res._id ? res : u))
                toast.success('Member profile synchronized successfully.')
                setShowEditModal(false)
            } else {
                toast.error(res.message || 'Synchronization failed.')
            }
        } catch (error) {
            toast.error('Server connection error.')
        } finally {
            setUpdating(false)
        }
    }

    const roleColors: Record<string, string> = {
        admin: 'bg-rose-500', agent: 'bg-emerald-500', customer: 'bg-blue-500',
        broker: 'bg-indigo-500', owner: 'bg-amber-500', developer: 'bg-slate-700'
    }

    const filtered = users.filter(u => {
        const matchesSearch = (u.name || '').toLowerCase().includes(search.toLowerCase()) ||
                              (u.email || '').toLowerCase().includes(search.toLowerCase());
        const matchesRole = roleFilter === 'all' || u.role === roleFilter;
        return matchesSearch && matchesRole;
    })

    const totalPages = Math.ceil(filtered.length / itemsPerPage);
    const paginatedUsers = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    useEffect(() => {
        setCurrentPage(1);
    }, [search, roleFilter, itemsPerPage]);

    const handleSelectUser = (id: string) => {
        setSelectedUsers(prev => prev.includes(id) ? prev.filter(uid => uid !== id) : [...prev, id]);
    };

    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setSelectedUsers(paginatedUsers.map(u => u._id));
        } else {
            setSelectedUsers([]);
        }
    };

    const handleBulkDelete = async () => {
        if (!token || selectedUsers.length === 0) return;
        setIsDeletingMultiple(true);
        setConfirmModal({ ...confirmModal, isOpen: false });
        
        try {
            const res = await api.adminDeleteMultipleUsers(token, selectedUsers);
            if (res.message === 'Users deleted successfully') {
                setUsers(prev => prev.filter(u => !selectedUsers.includes(u._id)));
                setSelectedUsers([]);
                toast.success('Selected identities have been purged.');
            } else {
                toast.error(res.message || 'Bulk purge failed.');
            }
        } catch (error) {
            toast.error('Server error during bulk deletion.');
        } finally {
            setIsDeletingMultiple(false);
        }
    };

    // Filter packages based on user role
    const filteredPkgs = packages.filter(p => {
        if (!selectedUser) return false;
        // In the package model type is ['developer', 'broker', 'owner']
        // Some users might have generic roles like 'agent' which we might map to 'broker' or 'owner'
        const role = selectedUser.role;
        if (role === 'developer') return p.type === 'developer';
        if (role === 'broker' || role === 'agent') return p.type === 'broker';
        if (role === 'owner') return p.type === 'owner';
        return true;
    })

    return (
        <AdminLayout>
            <div className="animate__animated animate__fadeIn">
                {/* Header Section */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                    <div>
                        <h1 className="text-3xl font-bold text-[#05144b] mb-2">Member Registry</h1>
                        <p className="text-[#05144b] text-[14px] font-semibold">Monitor user permissions, roles, and platform access protocols.</p>
                    </div>
                    <div className="hidden lg:flex items-center gap-4 px-6 py-3 bg-white dark:bg-slate-900 rounded-md border border-gray-100 dark:border-slate-800 shadow-xl">
                        <div className="flex -space-x-1.5">
                            {users.slice(0, 4).map((u, i) => (
                                <div key={i} className={`w-8 h-8 rounded-md border-2 border-white dark:border-slate-900 ${roleColors[u.role] || 'bg-[#05144b]'} flex items-center justify-center text-[9px] font-bold text-white shadow-sm`}>
                                    {u.name?.charAt(0).toUpperCase()}
                                </div>
                            ))}
                        </div>
                        <span className="text-[12px] font-bold text-black">{users.length} Platform members active</span>
                    </div>
                </div>
                 
                {/* Main Table Container */}
                <div className="bg-white dark:bg-slate-900 rounded-md border border-gray-100 dark:border-slate-800 shadow-sm overflow-hidden">
                    {/* Table Header / Toolbar */}
                    <div className="p-8 border-b border-gray-100 dark:border-slate-800 flex flex-col lg:flex-row lg:items-center justify-between gap-6 bg-white/50 dark:bg-slate-800/30">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-white border border-[#05144b] text-[#05144b] rounded-md flex items-center justify-center shadow-xl">
                                <i className="fa-solid fa-user-shield text-lg"></i>
                            </div>
                            <div>
                                <h3 className="text-xl md:text-2xl font-bold text-[#05144b] leading-tight">Access Control Stream</h3>
                                <p className="text-[13px] md:text-[14px] font-semibold text-[#05144b]">{filtered.length} identities synchronized</p>
                            </div>
                        </div>
                      
                         <div className="flex flex-col lg:flex-row items-center gap-4 w-full lg:w-auto">
                            {selectedUsers.length > 0 && (
                                <button
                                    onClick={() => setConfirmModal({ isOpen: true, id: '', name: '', type: 'bulk_user' })}
                                    className="bg-rose-500 hover:bg-rose-600 text-white border-none rounded-md py-4 px-6 text-[13px] font-bold shadow-sm transition-all flex items-center gap-2 active:scale-95"
                                >
                                    <i className="fa-solid fa-trash"></i>
                                    Delete Selected ({selectedUsers.length})
                                </button>
                            )}
                            <select 
                                className="bg-white border border-gray-100 rounded-md py-4 px-6 text-[13px] font-bold text-[#05144b] focus:ring-2 focus:ring-[#05144b] shadow-sm appearance-none cursor-pointer outline-none w-full lg:w-32"
                                value={itemsPerPage}
                                onChange={e => setItemsPerPage(Number(e.target.value))}
                            >
                                <option value={10}>10 / page</option>
                                <option value={20}>20 / page</option>
                                <option value={50}>50 / page</option>
                                <option value={100}>100 / page</option>
                            </select>

                            <select 
                                className="bg-white border border-gray-100 rounded-md py-4 px-6 text-[13px] font-bold text-[#05144b] focus:ring-2 focus:ring-[#05144b] shadow-sm appearance-none cursor-pointer outline-none w-full lg:w-48"
                                value={roleFilter}
                                onChange={e => setRoleFilter(e.target.value)}
                            >
                                <option value="all">All Access Roles</option>
                                <option value="admin">Administrators</option>
                                <option value="broker">Brokers / Agents</option>
                                <option value="developer">Developers</option>
                                <option value="owner">Property Owners</option>
                                <option value="buyer">Buyers / Tenants</option>
                            </select>

                            <div className="relative group max-w-lg w-full">
                                <i className="fa-solid fa-magnifying-glass absolute left-5 top-1/2 -translate-y-1/2 text-black/20 group-focus-within:text-black transition-colors text-xs"></i>
                                <input
                                    type="text"
                                    className="w-full bg-white dark:bg-slate-800 border border-gray-100 rounded-md py-4 pl-12 pr-12 text-[13px] font-semibold text-[#05144b] focus:ring-2 focus:ring-[#05144b] shadow-inner appearance-none cursor-pointer transition-all"
                                    placeholder="Search member directory..."
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-32">
                             <div className="w-10 h-10 border-4 border-[#05144b] border-t-transparent rounded-full animate-spin mb-6"></div>
                            <p className="text-[#05144b] font-semibold text-[14px]">Parsing Identity Database...</p>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-32 px-6 text-center">
                            <div className="w-18 h-18 bg-white dark:bg-slate-800 rounded-full flex items-center justify-center mb-6 border-2 border-gray-100 dark:border-slate-700 shadow-inner">
                                <i className="fa-solid fa-user-slash text-2xl text-black/10"></i>
                            </div>
                            <h5 className="text-xl font-bold text-black tracking-tight mb-2">
                                {search ? `No Results for "${search}"` : 'The Directory is Empty'}
                            </h5>
                            <p className="text-black/40 text-[13px] font-bold">Verify search criteria or recruit new members.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto scrollbar-hide">
                            <table className="w-full text-left border-collapse">
                                 <thead>
                                     <tr className="border-b border-slate-100 dark:border-slate-800 text-left text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                                         <th className="px-4 py-4 w-[50px]">
                                             <input 
                                                 type="checkbox" 
                                                 onChange={handleSelectAll}
                                                 checked={paginatedUsers.length > 0 && selectedUsers.length === paginatedUsers.length}
                                                 className="rounded border-gray-300 text-[#05144b] focus:ring-[#05144b] w-4 h-4 cursor-pointer"
                                             />
                                         </th>
                                         <th className="px-4 py-4">Identity</th>
                                         <th className="px-4 py-4">Contact</th>
                                         <th className="px-4 py-4">Role</th>
                                         <th className="px-4 py-4">Max Devices</th>
                                         <th className="px-4 py-4">Joined</th>
                                         <th className="px-4 py-4 text-right">Actions</th>
                                     </tr>
                                 </thead>
                                 <tbody className="divide-y divide-slate-100/50 dark:divide-slate-800/50">
                                    {paginatedUsers.map(u => (
                                        <tr key={u._id} className="hover:bg-white/80 dark:hover:bg-slate-800/30 transition-all group">
                                             <td className="px-4 py-6 whitespace-nowrap">
                                                 <input 
                                                     type="checkbox"
                                                     checked={selectedUsers.includes(u._id)}
                                                     onChange={() => handleSelectUser(u._id)}
                                                     className="rounded border-gray-300 text-[#05144b] focus:ring-[#05144b] w-4 h-4 cursor-pointer"
                                                 />
                                             </td>
                                             <td className="px-4 py-6 whitespace-nowrap">
    <div className="flex items-center gap-4">

        <div className={`w-11 h-11 rounded-md ${roleColors[u.role] || 'bg-[#05144b]'} flex items-center justify-center text-white font-bold text-lg shadow-lg`}>
            {u.name?.charAt(0).toUpperCase()}
        </div>

        <div>
            <div className="flex items-center gap-2">
                <h4 className="font-bold text-[#05144b] text-[15px]">{u.name}</h4>

                <button
                    onClick={() => handleLoginAsUser(u._id)}
                    className="text-[10px] bg-white border-2 border-[#05144b] text-[#05144b] px-2 py-1 rounded hover:bg-blue-50 font-bold"
                >
                    Login As
                </button>
            </div>

            <div className="text-[12px] font-medium text-[#05144b]/60 mt-1">{u.email}</div>
        </div>

    </div>
</td>
                                             <td className="px-4 py-6 whitespace-nowrap">
                                                <div className="flex flex-col">
                                                    <span className="text-[13px] font-bold text-black">{u.email}</span>
                                                    <span className="text-[11px] font-bold text-black/40 mt-0.5">{u.phone || 'No phone linked'}</span>
                                                </div>
                                            </td>
                                             <td className="px-4 py-6 whitespace-nowrap">
                                                <div className="relative inline-block w-44">
                                                    <select
                                                        className={`block w-full px-4 py-2.5 text-[12px] font-bold rounded-md border-2 appearance-none cursor-pointer outline-none transition-all
                                                            ${u.role === 'admin' ? 'bg-rose-50 border-rose-100 text-rose-700' :
                                                                u.role === 'agent' ? 'bg-emerald-50 border-emerald-100 text-emerald-700' :
                                                                    'bg-white border-[#05144b] text-[#05144b]'}`}
                                                        value={u.role}
                                                        disabled={changingRole === u._id}
                                                        onChange={e => handleRoleChange(u._id, e.target.value)}
                                                    >
                                                        <option value="admin">Administrator</option>
                                                        <option value="broker">Certified Broker</option>
                                                        <option value="owner">Property Owner</option>
                                                        <option value="developer">Developer</option>
                                                        <option value="buyer">Buyer/Tenant</option>
                                                    </select>
                                                    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-black">
                                                        {changingRole === u._id ? (
                                                            <div className="w-3 h-3 border-2 border-[#05144b] border-t-transparent rounded-full animate-spin"></div>
                                                        ) : (
                                                            <i className="fa-solid fa-chevron-down text-[10px]"></i>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                             <td className="px-4 py-6 whitespace-nowrap">
                                                 <span className="text-[13px] font-bold text-black tabular-nums">
                                                     {u.role === 'admin' ? 'Unlimited' : (u.maxDevices !== undefined ? u.maxDevices : 5)}
                                                 </span>
                                             </td>
                                             <td className="px-4 py-6 whitespace-nowrap">
                                                <span className="text-[13px] font-bold text-black tabular-nums">
                                                    {u.createdAt ? new Date(u.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                                                </span>
                                            </td>
                                             <td className="px-4 py-6 whitespace-nowrap text-right">
                                                <div className="flex items-center justify-end gap-2.5">
                                                    {/* Package Assign Button */}
                                                    {(u.role === 'developer' || u.role === 'broker' || u.role === 'owner') && (
                                                        <button
                                                            onClick={() => handleOpenPkgModal(u)}
                                                            className={`w-10 h-10 rounded-md border transition-all flex items-center justify-center active:scale-95 shadow-sm text-sm
                                                                ${u.assignedPackage 
                                                                    ? 'bg-amber-50 border-amber-300 text-amber-600 hover:bg-amber-100' 
                                                                    : 'bg-white border-gray-200 text-gray-400 hover:border-amber-400 hover:text-amber-500'}`}
                                                            title={u.assignedPackage ? `Package: ${u.assignedPackage.name} (Click to change)` : 'Assign Package'}
                                                        >
                                                            <i className="fa-solid fa-crown text-sm"></i>
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => handleOpenEditModal(u)}
                                                         className="w-10 h-10 rounded-md bg-white border border-gray-100 text-black transition-all flex items-center justify-center active:scale-95 shadow-sm hover:border-[#05144b] hover:text-[#05144b]"
                                                        title="Refine profile"
                                                    >
                                                        <i className="fa-solid fa-user-pen text-sm"></i>
                                                    </button>
                                                    <button
                                                        disabled={deleting === u._id}
                                                        onClick={() => setConfirmModal({ isOpen: true, id: u._id, name: u.name, type: 'user' })}
                                                        className="w-10 h-10 rounded-md bg-white border border-gray-100 text-black transition-all flex items-center justify-center disabled:opacity-50 active:scale-95 shadow-sm hover:border-rose-600 hover:text-rose-600"
                                                        title="Revoke access"
                                                    >
                                                        {deleting === u._id
                                                            ? <div className="w-5 h-5 border-2 border-rose-600 border-t-transparent rounded-full animate-spin"></div>
                                                            : <i className="fa-solid fa-user-slash text-sm"></i>
                                                        }
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                         </div>
                    )}

                    {/* Pagination Footer */}
                    {!loading && filtered.length > 0 && (
                        <div className="p-6 bg-slate-50/50 dark:bg-slate-800/50 border-t border-gray-100 dark:border-slate-800 flex items-center justify-between">
                            <div className="text-[13px] font-bold text-[#05144b]/60">
                                Showing <span className="text-[#05144b]">{Math.min(filtered.length, (currentPage - 1) * itemsPerPage + 1)}</span> to <span className="text-[#05144b]">{Math.min(filtered.length, currentPage * itemsPerPage)}</span> of <span className="text-[#05144b]">{filtered.length}</span> members
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    disabled={currentPage === 1}
                                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                    className="w-10 h-10 rounded-md bg-white border border-gray-100 flex items-center justify-center text-[#05144b] disabled:opacity-30 disabled:cursor-not-allowed hover:bg-blue-50 transition-all shadow-sm active:scale-95"
                                >
                                    <i className="fa-solid fa-chevron-left text-xs"></i>
                                </button>
                                
                                {[...Array(totalPages)].map((_, i) => {
                                    const pageNum = i + 1;
                                    if (pageNum === 1 || pageNum === totalPages || (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)) {
                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => setCurrentPage(pageNum)}
                                                className={`w-10 h-10 rounded-md text-[13px] font-bold transition-all shadow-sm active:scale-95 ${currentPage === pageNum ? 'bg-[#05144b] text-white' : 'bg-white text-[#05144b] border border-gray-100 hover:bg-blue-50'}`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    }
                                    if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                                        return <span key={pageNum} className="px-2 text-slate-400">...</span>;
                                    }
                                    return null;
                                })}

                                <button
                                    disabled={currentPage === totalPages}
                                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                    className="w-10 h-10 rounded-md bg-white border border-gray-100 flex items-center justify-center text-[#05144b] disabled:opacity-30 disabled:cursor-not-allowed hover:bg-blue-50 transition-all shadow-sm active:scale-95"
                                >
                                    <i className="fa-solid fa-chevron-right text-xs"></i>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Package Assignment Modal */}
            <Modal show={showPkgModal} onHide={() => setShowPkgModal(false)} centered className="custom-premium-modal">
                <Modal.Body className="p-0">
                    <div className="bg-white dark:bg-slate-900 rounded-md overflow-hidden border border-gray-100 dark:border-slate-800 shadow-2xl">
                        <div className="p-6 border-b border-gray-100 dark:border-slate-800 flex items-center justify-between bg-white/50 dark:bg-slate-800/30">
                            <div className="flex items-center gap-4">
                                <div className="w-11 h-11 bg-white border border-[#05144b] text-[#05144b] rounded-md flex items-center justify-center shadow-lg">
                                    <i className="fa-solid fa-gem text-lg"></i>
                                </div>
                                <div>
                                    <h5 className="text-xl font-bold text-black tracking-tight">Package Protocol</h5>
                                    <p className="text-[13px] font-bold text-black/60 mt-0.5">Member: {selectedUser?.name}</p>
                                </div>
                            </div>
                            <button onClick={() => setShowPkgModal(false)} className="w-9 h-9 rounded-full hover:bg-gray-100 text-black transition-colors flex items-center justify-center">
                                <i className="fa-solid fa-xmark text-lg"></i>
                            </button>
                        </div>

                        <div className="p-8 space-y-6">
                            <div className="space-y-2">
                                <label className="text-[13px] font-bold text-black ml-1">Plan Selection</label>
                                <select
                                     className="block w-full px-5 py-3.5 bg-white border border-gray-100 focus:ring-2 focus:ring-[#05144b] focus:border-[#05144b] rounded-md text-[15px] font-bold text-black transition-all outline-none appearance-none cursor-pointer"
                                    value={selectedPkgId}
                                    onChange={(e) => setSelectedPkgId(e.target.value)}
                                >
                                    <option value="">-- Choose Package --</option>
                                    {filteredPkgs.map(pkg => (
                                        <option key={pkg._id} value={pkg._id}>{pkg.name} - ₹{pkg.price}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="grid grid-cols-2 gap-5">
                                <div className="space-y-1.5">
                                    <label className="text-[13px] font-bold text-black ml-1">Begin Cycle</label>
                                    <input
                                        type="date"
                                         className="block w-full px-5 py-3.5 bg-white border border-gray-100 focus:ring-2 focus:ring-[#05144b] focus:border-[#05144b] rounded-md text-[15px] font-bold text-black transition-all outline-none"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[13px] font-bold text-black ml-1">Expiry Point</label>
                                    <input
                                        type="date"
                                         className="block w-full px-5 py-3.5 bg-white border border-gray-100 focus:ring-2 focus:ring-[#05144b] focus:border-[#05144b] rounded-md text-[15px] font-bold text-black transition-all outline-none"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="p-8 pt-0 flex flex-wrap gap-4">
                            {selectedUser?.assignedPackage && (
                                <button
                                    onClick={() => setConfirmModal({ isOpen: true, id: selectedUser._id, name: selectedUser.name, type: 'package' })}
                                    disabled={assigning}
                                    className="flex-1 py-4 text-rose-600 bg-rose-50 hover:bg-rose-600 hover:text-white text-[13px] font-bold rounded-md border-2 border-rose-100 transition-all disabled:opacity-50"
                                >
                                    Purge plan
                                </button>
                            )}
                            <button
                                onClick={handleAssignPkg}
                                disabled={assigning || !selectedPkgId}
                                className="flex-[2] py-4 bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 text-[14px] font-bold rounded-md shadow-lg active:scale-95 transition-all disabled:opacity-50"
                            >
                                {assigning ? 'Initializing...' : 'Confirm Protocol'}
                            </button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Profile Edit Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="lg" className="custom-premium-modal">
                <Modal.Body className="p-0">
                    <Form onSubmit={handleEditSubmit} className="bg-white dark:bg-slate-900 rounded-md overflow-hidden border border-gray-100 dark:border-slate-800 shadow-2xl">
                        <div className="p-6 border-b border-gray-100 dark:border-slate-800 flex items-center justify-between bg-white/50 dark:bg-slate-800/30">
                            <div className="flex items-center gap-4">
                                <div className="w-11 h-11 bg-white border border-[#05144b] text-[#05144b] rounded-md flex items-center justify-center shadow-lg">
                                    <i className="fa-solid fa-user-pen text-lg"></i>
                                </div>
                                <div>
                                    <h5 className="text-xl font-bold text-black tracking-tight">Account Refinement</h5>
                                    <p className="text-[13px] font-bold text-black/60 mt-0.5">Overwriting: {editingUser.name}</p>
                                </div>
                            </div>
                            <button type="button" onClick={() => setShowEditModal(false)} className="w-9 h-9 rounded-full hover:bg-gray-100 text-black transition-colors flex items-center justify-center">
                                <i className="fa-solid fa-xmark text-lg"></i>
                            </button>
                        </div>

                        <div className="p-8 space-y-8 max-h-[70vh] overflow-y-auto scrollbar-hide">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-1.5">
                                    <label className="text-[13px] font-bold text-black ml-1">Legal Name</label>
                                    <input
                                        type="text"
                                        required
                                         className="block w-full px-5 py-3.5 bg-white border border-gray-100 focus:ring-2 focus:ring-[#05144b] focus:border-[#05144b] rounded-md text-[15px] font-bold text-black outline-none transition-all"
                                        value={editingUser.name || ''}
                                        onChange={e => setEditingUser({ ...editingUser, name: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[13px] font-bold text-black ml-1">Email Address</label>
                                    <input
                                        type="email"
                                        required
                                         className="block w-full px-5 py-3.5 bg-white border border-gray-100 focus:ring-2 focus:ring-[#05144b] focus:border-[#05144b] rounded-md text-[15px] font-bold text-black outline-none transition-all"
                                        value={editingUser.email || ''}
                                        onChange={e => setEditingUser({ ...editingUser, email: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[13px] font-bold text-black ml-1">Primary Contact</label>
                                    <input
                                        type="text"
                                         className="block w-full px-5 py-3.5 bg-white border border-gray-100 focus:ring-2 focus:ring-[#05144b] focus:border-[#05144b] rounded-md text-[15px] font-bold text-black outline-none transition-all"
                                        value={editingUser.phone || ''}
                                        onChange={e => setEditingUser({ ...editingUser, phone: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[13px] font-bold text-black ml-1">Regional Zone (City)</label>
                                    <input
                                        type="text"
                                         className="block w-full px-5 py-3.5 bg-white border border-gray-100 focus:ring-2 focus:ring-[#05144b] focus:border-[#05144b] rounded-md text-[15px] font-bold text-black outline-none transition-all"
                                        value={editingUser.city || ''}
                                        onChange={e => setEditingUser({ ...editingUser, city: e.target.value })}
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="text-[13px] font-bold text-black ml-1">Member Role</label>
                                    <select
                                         className="bg-gray-100 text-[#05144b] px-4 py-2 rounded-md font-bold text-[12px] border-none focus:ring-2 focus:ring-[#05144b] focus:border-[#05144b] w-full"
                                        value={editingUser.role || ''}
                                        onChange={e => setEditingUser({ ...editingUser, role: e.target.value })}
                                    >
                                        <option value="admin">Administrator</option>
                                        <option value="broker">Certified Broker</option>
                                        <option value="owner">Property Owner</option>
                                        <option value="developer">Developer</option>
                                        <option value="buyer">Buyer/Tenant</option>
                                    </select>
                                </div>
                                {editingUser.role !== 'admin' && (
                                    <div className="space-y-1.5">
                                        <label className="text-[13px] font-bold text-black ml-1">Max Devices Limit</label>
                                        <input
                                            type="number"
                                            min="1"
                                             className="block w-full px-5 py-3.5 bg-white border border-gray-100 focus:ring-2 focus:ring-[#05144b] focus:border-[#05144b] rounded-md text-[15px] font-bold text-black outline-none transition-all"
                                            value={editingUser.maxDevices !== undefined ? editingUser.maxDevices : 5}
                                            onChange={e => setEditingUser({ ...editingUser, maxDevices: Number(e.target.value) })}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="p-8 border-t border-gray-100 dark:border-slate-800 flex gap-4 bg-white/50 dark:bg-slate-800/30">
                            <button
                                type="button"
                                onClick={() => setShowEditModal(false)}
                                className="flex-1 py-4 text-[14px] font-bold text-black hover:text-black/60 transition-colors"
                            >
                                Discard Sync
                            </button>
                            <button
                                type="submit"
                                disabled={updating}
                                className="flex-[2] py-4 bg-white border-2 border-[#05144b] text-[#05144b] hover:bg-blue-50 text-[14px] font-bold rounded-md shadow-lg active:scale-95 transition-all flex items-center justify-center disabled:opacity-50"
                            >
                                {updating ? 'Syncing...' : 'Update Member Profile'}
                            </button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            <ConfirmModal 
                isOpen={confirmModal.isOpen}
                title={confirmModal.type === 'package' ? 'Revoke Package' : confirmModal.type === 'bulk_user' ? 'Purge Selected Identities' : 'Purge Identity'}
                message={confirmModal.type === 'package' 
                    ? `Are you sure you want to remove the current package from ${confirmModal.name}?` 
                    : confirmModal.type === 'bulk_user'
                    ? `Are you sure you want to delete ${selectedUsers.length} selected users? This action cannot be undone.`
                    : `Are you sure you want to delete user "${confirmModal.name}"? This will also delete all their properties.`}
                onConfirm={confirmModal.type === 'package' ? handleUnassignPkg : confirmModal.type === 'bulk_user' ? handleBulkDelete : handleDelete}
                onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
                confirmText={confirmModal.type === 'package' ? 'Revoke Now' : 'Purge'}
                cancelText="Cancel"
                type="danger"
            />
        </AdminLayout>
    )
}
